/* global CVP */
/* jshint -W040, curly:false */

// # AuthManager

'use strict';

var _ = require('./vendor/underscore');
var Promise = require('./vendor/machina.promise');
var App = require('./app');
var ENV = require('./env');
var _logger = require('./logger');
var AdobeFlashAuth = require('./adobeflashauth');
var AdobeHTML5Auth = require('./adobehtml5auth');
var AdobeClientlessAuth = require('./adobeclientlessauth');
var ClientAuthNFsm = require('./clientauthnfsm');
var ClientAuthZFsm = require('./clientauthzfsm');
var ClientlessAuthNFsm = require('./clientlessauthnfsm');
var ClientlessAuthZFsm = require('./clientlessauthzfsm');
var AspenController = require('./aspencontroller');
var SimpleEmitter = require('./emitter');
var Util = require('./util');
var Dom = require('./dom');
var MVPDConfig = require('./mvpdconfig');
var MVPDPicker = require('./mvpdpicker');
var Store = require('./store');
var RememberedProvider = require('./remembered');
var LoginIframe = require('./loginiframe');
var LoginPopup = require('./loginpopup');
var LoginWindow = require('./loginwindow');


var AuthManager = new SimpleEmitter();
var FLASH = App.FLASH;
var HTML5 = App.HTML5;
var CLIENTLESS = App.CLIENTLESS;
var authNFsm;
var authZFsm;
var loginMethod;
var Login = {
	UNDEFINED: 0,
	TEMPPASS: 1,
	REDIRECT: 2,
	IFRAME: 3,
	POPUP: 4,
	WINDOW: 5
};
var defaults = {
	configEnv: App.CONFIG_ENV_PROD,
	vendorEnv: App.VENDOR_ENV_PROD,
	primaryMinCount: App.MIN_LOGOS_TO_DISPLAY,
	primaryMaxCount: App.MAX_LOGOS_TO_DISPLAY,
	hiddenMvpdIds: [ 'TempPass' ],
	autoAuth: false,
	autoAuthMvpdId: 'TempPass',
	handleRedirect: true,
	refreshlessLogin: false,
	refreshlessLogout: false
};
var config = {};
var inited = false;
var isAuthNed = false;
var auth = null;
var authReadyPromise = new Promise();
var geoReadyPromise = new Promise();
var libReadyPromise = Promise.all(MVPDConfig.ready, geoReadyPromise, authReadyPromise);
var isReady = false;
var accessEnablerOrigin = '';
var currentMvpdId = null;
var nullProviderOnClose = false;
var geoCountry;
var preauthZRequest = null;
var preauthZResourceDelimiter = ',';

// Map "Adobe requestorId"/"brand clientId" to CVP site for configs
// Assume lowercased value is OK, but there are exceptions...
var getSiteId = (function () {
	var requestorIdToSiteIdMap = {
		adultswim: 'as',
		cartoonnetwork: 'cn',
		trutv: 'tru'
	};
	return function convertRequestorIdToSiteId(adobeRequestorId) {
		var clientLower = adobeRequestorId.toLowerCase();
		return requestorIdToSiteIdMap[clientLower] || clientLower;
	};
}());

function getConfiguredEnvironment() {
	if (auth) return auth.getConfiguredEnvironment();
}

function _doesMvpdIdDisapprove(mvpdId) {

	// Short-circuit for null.
	if (!mvpdId) {
		return false;
	}

	var disapprovingMvpd = _.contains(App.DISAPPROVING_MVPDS, mvpdId);

	// Short-circuit if MVPD does not care about cross-environment contamination.
	if (!disapprovingMvpd) {
		return false;
	}

	var configedEnv = getConfiguredEnvironment();
	var overrideEnv = ENV.getVendorOverrideParam();
	var changingEnvs = overrideEnv && configedEnv && overrideEnv !== configedEnv || false;
	var switchingToProd = changingEnvs && overrideEnv === App.VENDOR_ENV_PROD || false;
	var disallowedCrossEnvironment = switchingToProd && disapprovingMvpd;

	_logger.info('MVPD ID: ' + mvpdId + ', configured: ' + configedEnv + ', override: ' + overrideEnv + ', changing? ' + changingEnvs + ', switching to prod? ' + switchingToProd + ', disallowed cross-environment contamination? ' + disallowedCrossEnvironment);

	return disallowedCrossEnvironment;
}

// Cache look-ups.
var doesMvpdIdDisapprove = _.memoize(_doesMvpdIdDisapprove);

function doesMvpdDisapprove(mvpd) {
	return doesMvpdIdDisapprove(mvpd.ID);
}

function doesCurrentMvpdIdDisapprove() {
	return doesMvpdIdDisapprove(currentMvpdId);
}

function initAuth(options) {
	var AuthClass;

	if (!_.isUndefined(options.authClass)) {
		switch (options.authClass) {
			case FLASH:
				AuthClass = AdobeFlashAuth;
			break;
			case HTML5:
				AuthClass = AdobeHTML5Auth;
			break;
			case CLIENTLESS:
				AuthClass = AdobeClientlessAuth;
			break;
		}
	}

	// If not provided, then base upon browser capability.
	if (!AuthClass) {
		if (CVP.swfobject.hasFlashPlayerVersion(App.REQUIRED_FLASH_VERSION)) {
			AuthClass = AdobeFlashAuth;
		} else if ('postMessage' in window && 'localStorage' in window) {
			AuthClass = AdobeHTML5Auth;
		} else {
			throw new Error('AdobeAuth failed to detect FlashPlayer for Flash AccessEnabler or postMessage + localStorage for HTML5 AccessEnabler');
		}
	}

	// Select the appropriate FSM.
	switch (AuthClass) {
		case AdobeClientlessAuth:
			authNFsm = new ClientlessAuthNFsm();
			authZFsm = new ClientlessAuthZFsm();
		break;
		case AdobeFlashAuth:
		case AdobeHTML5Auth:
			options.platform = 'flash';
			authNFsm = new ClientAuthNFsm();
			authZFsm = new ClientAuthZFsm();
		break;
	}

	auth = new AuthClass(options);
}

function notifyNotReady() {
	_logger.warn('AuthManager is not ready.');
}

function notifyDisapproved() {
	_logger.warn('Selected MVPD disapproves access.  All Auth API is no-op.');
}

// ### Public API

function getEnv() {
	if (!isReady) return notifyNotReady();

	return auth.getEnv();
}

function getCountryCode() {
	if (!isReady) return notifyNotReady();

	return geoCountry;
}

function isVisibleMvpdId(mvpdId) {
	if (!isReady) return notifyNotReady();

	return auth.isVisibleMvpdId(mvpdId);
}

function isValidMvpdId(mvpdId) {
	if (!isReady) return notifyNotReady();

	return auth.isValidMvpdId(mvpdId);
}

// `checkAuthentication`
// Checks for authN, not expected to prompt login query.
function checkAuthentication() {
	_logger.log('AuthManager:checkAuthentication');

	if (!isReady) return notifyNotReady();

	auth.checkAuthentication();
}

// `getAuthentication`
// Gets authN, is expected to prompt login query.
function getAuthentication(redirectUrl) {
	_logger.log('AuthManager:getAuthentication', arguments);

	if (!isReady) return notifyNotReady();

	// fix for hash/fragment URLs
	redirectUrl = Util.createRedirectUrl(redirectUrl);

	auth.getAuthentication(redirectUrl);
}

// `checkPreauthorizedResources(resourceIds)`
// Note: You cannot call this if you are not authenticated!
function checkPreauthorizedResources(resourceIds) {
	_logger.log('AuthManager:checkPreauthorizedResources', arguments);

	if (!isReady) return notifyNotReady();

	if (doesCurrentMvpdIdDisapprove()) {
		return notifyDisapproved();
	}

	// If first argument is a string, instead of an array, assume that
	// the list of arguments is a list of strings and convert to array.
	if (_.isString(resourceIds)) {
		resourceIds = _.toArray(arguments);
	}

	if (!_.isArray(resourceIds)) {
		throw new Error('AuthManager:checkPreauthorizedResources requires an array, instead of: ' + resourceIds);
	}

	// Sort IDs. Remove dupes, just in case.
	// We want to avoid invalidating the preauthz cache, if possible.
	resourceIds = _.uniq(resourceIds.sort());

	// Throw an error if more than 5 IDs are requested.
	if (resourceIds.length > 5) {
		throw new Error('AuthManager:checkPreauthorizedResources has a hard limit of five IDs, you provided: ' + resourceIds.length);
	}

	// If we have a previous record, log a warning that the preauthz cache is being invalidated.
	if (preauthZRequest && resourceIds.join(preauthZResourceDelimiter) !== preauthZRequest.join(preauthZResourceDelimiter)) {
		_logger.warn('AuthManager:checkPreauthorizedResources has already been requested with "' + preauthZRequest + '", by requesting "' + resourceIds + '" you have invalidated the preauthz cache.');
	}

	// Keep a record of what IDs were requested.
	preauthZRequest = resourceIds;

	auth.checkPreauthorizedResources(resourceIds);
}

function checkFeature(feature) {
	_logger.log('AuthManager:checkFeature', arguments);

	if (!isReady) {
		notifyNotReady();
		return false;
	}

	if (doesCurrentMvpdIdDisapprove()) {
		notifyDisapproved();
		return false;
	}

	return auth.checkFeature(feature);
}

// `checkAuthorization`
// Check for authorization to a given resource.
// (Not expected to prompt authN login.)
// Param `feature` is optional.
function checkAuthorization(resourceId, feature) {
	_logger.log('AuthManager:checkAuthorization', arguments);

	if (!isReady) return notifyNotReady();

	if (doesCurrentMvpdIdDisapprove()) {
		return notifyDisapproved();
	}

	if (!feature) {
		return auth.checkAuthorization(resourceId);
	}

	if (!checkFeature(feature)) {
		_logger.warn('AuthManager:checkAuthorization', 'feature "' + feature + '" was not found in feature list');
		this.emit('errorEvent', {
			source: 'cvp',
			data: {
				errorId: 'Z220',
				level: 'error',
				message: 'Did not call checkAuthorization because feature "' + feature + '" was not found in feature list'
			}
		});
	}
	else {
		auth.checkAuthorization(resourceId, feature);
	}
}

// `checkRegistrationCode(regCode)`
function checkRegistrationCode(regCode) {
	_logger.log('AuthManager:checkRegistrationCode', arguments);

	if (!isReady) return notifyNotReady();

	auth.checkRegistrationCode(regCode);
}

// `getMetadata`
// Request metadata from Adobe.
// The results will be returned via onMetadataStatus callback.
function getMetadata(/* key, args... */) {
	_logger.log('AuthManager:getMetadata', arguments);

	if (!isReady) return notifyNotReady();

	if (doesCurrentMvpdIdDisapprove()) {
		return notifyDisapproved();
	}

	auth.getMetadata.apply(auth, arguments);
}

function getFeatureList() {
	_logger.log('AuthManager:getFeatureList', arguments);

	if (!isReady) {
		notifyNotReady();
		return null;
	}

	if (doesCurrentMvpdIdDisapprove()) {
		notifyDisapproved();
		return null;
	}

	return auth.getFeatureList();
}

// `getAccessToken`
// gets the full access token object that needs
// to be sent on to the video player
function getAccessToken(options) {
	_logger.log('AuthManager:getAccessToken', arguments);

	var tokenObj = auth.getAccessToken();

	if (_.isEmpty(tokenObj)) {
		return null;
	}

	if (_.isObject(options)) {
		if ('accessToken' in tokenObj) {
			if (_.isString(options.prefix)) {
				tokenObj.accessToken = options.prefix + tokenObj.accessToken;
			}
			if (_.isString(options.suffix)) {
				tokenObj.accessToken = tokenObj.accessToken + options.suffix;
			}
		}
	}

	return tokenObj;
}

// `selectProvider`
// allows the user to select the provider
function selectProvider(mvpdId) {
	_logger.log('AuthManager:selectProvider', arguments);

	if (!isReady) return notifyNotReady();

	if (doesMvpdIdDisapprove(mvpdId)) {
		return notifyDisapproved();
	}

	if (mvpdId === null) {
		this.emit('nullProvider');
	}

	auth.setSelectedProvider(mvpdId);
}

// `getProvider`
// allows the user to find out the authentication status asynchronously
function getProvider(callback, context) {
	_logger.log('AuthManager:getProvider');

	if (!isReady) return notifyNotReady();

	auth.getSelectedProvider(callback, context);
}

// `getProviderID`
// allows the user to retrieve the specific provider id asynchronously
function getProviderID(callback, context) {
	_logger.log('AuthManager:getProviderID');

	if (!isReady) return notifyNotReady();

	auth.getProviderID(callback, context);
}

// `logout`
function logout() {
	_logger.log('AuthManager:logout');

	if (!isReady) return notifyNotReady();

	auth.logout();
}

// `getMvpdById`
function getMvpdById(mvpdId) {
	_logger.log('AuthManager:getMvpdById', arguments);

	if (!isReady) return notifyNotReady();

	return auth.getMvpdById(mvpdId);
}

// `getMvpdConfigEntry`
function getMvpdConfigEntry(mvpdId) {
	_logger.log('AuthManager:getMvpdConfigEntry', arguments);

	if (!isReady) return notifyNotReady();

	return auth.getMvpdConfigEntry(mvpdId);
}

// `getConfigString`
function getConfigString() {
	_logger.log('AuthManager:getConfigString', arguments);

	if (!isReady) return notifyNotReady();

	return auth.getConfigString();
}

var afterWindowOpens;
var afterPopupOpens;

function openLoginWindow(mvpd, options) {
	_logger.log('AuthManager:openLoginWindow', arguments);

	// Set selected provider after window has opened.
	afterWindowOpens = function () {
		LoginWindow.off('opened', afterWindowOpens, this);
		this.selectProvider(mvpd.ID);
	};

	LoginWindow.on('opened', afterWindowOpens, this);

	LoginWindow.open(mvpd, options, accessEnablerOrigin);
}

function closeLoginWindow() {
	LoginWindow.close();
	LoginWindow.off('opened', afterWindowOpens, this);
}

function openLoginPopup(mvpd, options) {
	_logger.log('AuthManager:openLoginPopup', arguments);

	// Set selected provider after window has opened.
	afterPopupOpens = function () {
		LoginPopup.off('opened', afterPopupOpens, this);
		this.selectProvider(mvpd.ID);
	};

	LoginPopup.on('opened', afterPopupOpens, this);

	LoginPopup.open(mvpd, options, accessEnablerOrigin);
}

function closeLoginPopup() {
	LoginPopup.close();
	LoginPopup.off('opened', afterPopupOpens, this);
}

function openLogin(mvpdId, options) {
	_logger.log('AuthManager:openLogin', arguments);

	// Cannot use new popup/tab/window for Clientless; SAML redirects new window instead of parent!
	// Calling selectProvider and expecting mock ClientlessAccessEnabler to open iframe login.
	if (auth instanceof AdobeClientlessAuth) {
		return this.selectProvider(mvpdId);
	}

	// Clean up previous login method.
	if (loginMethod === Login.POPUP) {
		closeLoginPopup();
	}

	if (loginMethod === Login.WINDOW) {
		closeLoginWindow();
	}

	// reset flag
	loginMethod = Login.UNDEFINED;

	// Short-circuit for TempPass: no iframe/popup/window.
	if (_.contains(config.hiddenMvpdIds, mvpdId)) {
		this.selectProvider(mvpdId);
		return;
	}

	// look up the MVPD in our filtered MVPDs
	var mvpd = getMvpdById(mvpdId);

	if (!mvpd) {
		throw new Error('Requested MVPD ID "' + mvpdId + '" was not found in the MVPD config or is disabled.');
	}

	// if the MVPD has a destinationURL, set location.href
	if (_.isString(mvpd.destinationURL)) {
		this.emit('overrideRedirect');

		location.href = mvpd.destinationURL;
		return;
	}

	if (config.refreshlessLogin && !mvpd.iFrameRequired) {
		// refreshless: redirect logins open in new window
		loginMethod = Login.WINDOW;
		openLoginWindow.call(this, mvpd, options);
	}
	else if (mvpd.iFrameRequired) {
		var loginType = ENV.determineLoginType({
			mvpdId: mvpdId,
			requestorId: config.requestorId
		});

		if (loginType === 'popup') {
			loginMethod = Login.POPUP;
			openLoginPopup.call(this, mvpd, options);
		}
		// else, close picker and selectProvider
		else {
			this.selectProvider(mvpdId);
		}
	}
	else {
		this.emit('loginRedirect');
		loginMethod = Login.REDIRECT;
		this.selectProvider(mvpdId);
	}
}

function byPrimaryOrder(mvpd) {
	return 'primaryOrder' in mvpd && mvpd.primaryOrder > 0 && 'picker' in mvpd;
}

function createPrimaryFilterForCountry(country) {
	return function byPrimaryOrderForCountry(mvpd) {
		return 'countries' in mvpd && _.contains(mvpd.countries, country) && byPrimaryOrder(mvpd);
	};
}

var defaultCountryFilter = createPrimaryFilterForCountry(App.DEFAULT_COUNTRY);
var defaultPrimaries;

function getDefaultPrimaries(mvpds) {
	return defaultPrimaries || (defaultPrimaries = _.filter(mvpds, defaultCountryFilter));
}

var countryPrimaries;

function getCountryPrimaries(mvpds, country) {
	return countryPrimaries || (countryPrimaries = _.filter(mvpds, createPrimaryFilterForCountry(country)));
}

function getPrimaries(mvpds) {
	var country = getCountryCode();

	if (country === App.UNKNOWN_COUNTRY || country === App.DEFAULT_COUNTRY) {
		return getDefaultPrimaries(mvpds);
	}

	var list = getCountryPrimaries(mvpds, country);
	if (!list.length) {
		list = getDefaultPrimaries(mvpds);
	}
	return list;
}

function openPicker(mvpds) {
	_logger.log('AuthManager:openPicker');

	if (!isReady) return notifyNotReady();

	if (!mvpds) {
		// explicitly calling this function without params
		// will cause this function to be called again with
		// a list of the last known MVPDs
		_logger.log('AuthManager:openPicker - forcing picker open');

		auth.displayProviderDialog();
	}
	else {
		_logger.log('AuthManager:openPicker - providing picker with mvpds');

		MVPDPicker.open({
			Strings: App.Strings,
			onRender: config.onPickerRendered,
			hasWelcomeSlate: !!(config.hasWelcomeSlate),
			primaryMinCount: Number(config.primaryMinCount),
			primaryMaxCount: Number(config.primaryMaxCount),
			primaryMvpds: getPrimaries(mvpds),
			mvpds: mvpds
		});
	}
}

function closeLogin() {
	_logger.log('AuthManager:closeLogin');

	// Yeah, it could be smarter, but it works, right?
	LoginWindow.close();
	LoginPopup.close();
	LoginIframe.close();
}

function closePicker() {
	_logger.log('AuthManager:closePicker');

	MVPDPicker.close();
}

function getMVPDs() {
	if (auth) return auth.getMVPDs();
}

function onInvalidProvider(obj) {
	_logger.log('AuthManager:onInvalidProvider', arguments);

	var shouldLogOut = true;

	if (_.isFunction(config.onInvalidProvider)) {
		shouldLogOut = config.onInvalidProvider(obj.id);
	}

	if (shouldLogOut) {
		logout();
	}
}

function onAuthInitFailure(obj) {
	_logger.log('AuthManager:onAuthInitFailure', arguments);

	authReadyPromise.reject(obj);
}

function getAdobeOrigin(accessEnablerUrl) {
	// if AccessEnabler is remote, use the origin of the AccessEnabler
	var originPattern = /^(?:https?:)?(\/\/[^\/]+\/).+$/;
	var originMatch = originPattern.exec(accessEnablerUrl);
	if (originMatch) {
		return accessEnablerUrl.replace(originMatch[0], location.protocol + originMatch[1]);
	}

	// if local AccessEnablerLoader.swf, and has "staging" query param, use staging origin
	var localLoaderForStagingPattern = /^\/[^/].+[?&]staging/;
	if (localLoaderForStagingPattern.test(accessEnablerUrl)) {
		return App.getOriginForEnv(App.VENDOR_ENV_STAGING);
	}

	// otherwise, use origin for the configured environment
	return App.getOriginForEnv(config.vendorEnv);
}

function onAuthInitReady(obj) {
	_logger.log('AuthManager:onAuthInitReady', arguments);

	isReady = true;

	// Relinquishing control of window domain to Adobe.
	accessEnablerOrigin = getAdobeOrigin(obj.accessEnablerUrl);

	// Configure TempPass (Auto-Auth).
	if (config.autoAuth && config.autoAuthMvpdId && authNFsm.supports.autoAuth) {
		if (this.getMvpdById(config.autoAuthMvpdId)) {
			authNFsm.setAutoAuth(true);
			authNFsm.setAutoAuthMvpdId(config.autoAuthMvpdId);

			if (config.autoAuthRetryLimit) {
				authNFsm.setAutoAuthRetryLimit(config.autoAuthRetryLimit);

				if (config.autoAuthRetryDelay) {
					authNFsm.setAutoAuthRetryDelay(config.autoAuthRetryDelay);
				}
			}
		}
		else {
			_logger.warn('AuthManager:onAuthInitReady', 'autoAuth was configured; however, "' + config.autoAuthMvpdId + '" was not a valid MVPD ID, so autoAuth is not available.');
		}
	}

	auth.getProviderID(function getSelectedProviderResponse(mvpdId) {
		currentMvpdId = mvpdId;
	});

	authReadyPromise.fulfill(obj);
}

function onAuthNPass(obj) {
	_logger.log('AuthManager:onAuthNPass', arguments);

	var mvpdId = obj.mvpdId;

	currentMvpdId = doesMvpdIdDisapprove(mvpdId) ? null : mvpdId;

	if (!currentMvpdId) {
		return logout();
	}

	isAuthNed = true;

	// Remember provider of successful login.
	if (isVisibleMvpdId(currentMvpdId)) {
		RememberedProvider.save(currentMvpdId);
	}

	this.emit('authNPass', _.omit(obj, ['mvpd']));
}

function onAuthNFail(obj) {
	_logger.log('AuthManager:onAuthNFail', arguments);

	// Forget cached mvpdId.
	currentMvpdId = null;

	isAuthNed = false;

	this.emit('authNFail', obj);
}

function emitPreauthorizedResource(resourceId) {
	this.emit('preauthorizedResource', { resource: resourceId });
}

function onPreauthorizedResources(obj) {
	_logger.log('AuthManager:onPreauthorizedResources', arguments);

	_logger.log('AuthManager:onPreauthorizedResources - getProviderID request');
	getProviderID(function getProviderIDResponse(providerId) {
		_logger.log('AuthManager:onPreauthorizedResources - getProviderID response');

		// Validate currently authN'ed provider.
		if (!providerId) {
			this.emit('errorEvent', {
				source: 'cvp',
				data: {
					errorId: 'Z201',
					level: 'error',
					message: 'Attempted to authorize with a provider that was not found in MVPD config.'
				}
			});

			return;
		}

		if (!_.isArray(obj.resources)) {
			_logger.error('AuthManager:onPreauthorizedResources - failed to receive array of resourceIDs!');
			return;
		}

		// Sort to match the order of the requested IDs.
		var preauthZResponse = obj.resources.sort();

		// Log the difference, just for curiousity's sake.
		if (preauthZResponse.join(preauthZResourceDelimiter) !== preauthZRequest.join(preauthZResourceDelimiter)) {
			var missingIds = _.difference(preauthZRequest, preauthZResponse);
			_logger.info('AuthManager:onPreauthorizedResources - some resourceIDs were not preauthorized: ' + missingIds);

			if (preauthZRequest.length !== preauthZResponse.length + missingIds.length) {
				var extraIds = _.difference(preauthZResponse, preauthZRequest);
				_logger.warn('AuthManager:onPreauthorizedResources - unexpected resourceIDs were preauthorized -- ' + extraIds + ' -- or concurrent requests were made resulting in a race condition.');
			}
		}

		// Emit the response as an array.
		this.emit('preauthorizedResources', { resources: preauthZResponse });

		if (!preauthZResponse.length) {
			_logger.debug('AuthManager:onPreauthorizedResources - no resourceIDs were preauthorized, "checkPreauthorizedResource" will not fire!');
			return;
		}

		// Emit a response for each successful resourceId.
		_.each(preauthZResponse, emitPreauthorizedResource, this);
	}, this);
}

function onAuthZPass(obj) {
	_logger.log('AuthManager:onAuthZPass', arguments);

	this.emit('authZPass', obj);
}

function onAuthZFail(obj) {
	_logger.log('AuthManager:onAuthZFail', arguments);

	this.emit('authZFail', obj);
}

function onMetadataStatus(obj) {
	_logger.log('AuthManager:onMetadataStatus', arguments);

	this.emit('metadataStatus', obj);
}

function onTrackingData(obj) {
	_logger.log('AuthManager:onTrackingData', arguments);

	this.emit('trackingData', obj);
}

function onAuthorizationDetection(obj) {
	_logger.log('AuthManager:onAuthorizationDetection', arguments);

	this.emit('authorizationDetection', obj);
}

function onAuthenticationDetection(obj) {
	_logger.log('AuthManager:onAuthenticationDetection', arguments);

	this.emit('authenticationDetection', obj);
}

function onMvpdSelection(obj) {
	_logger.log('AuthManager:onMvpdSelection', arguments);

	this.emit('mvpdSelection', obj);
}

function onRegistrationCodeValid(obj) {
	_logger.log('AuthManager:onRegistrationCodeValid', arguments);

	this.emit('registrationCodeValid', obj);
}

function onRegistrationCodeInvalid(obj) {
	_logger.log('AuthManager:onRegistrationCodeInvalid', arguments);

	this.emit('registrationCodeInvalid', obj);
}

function onSetMvpdRedirectURL(obj) {
	_logger.log('AuthManager:onSetMvpdRedirectURL', arguments);

	var url = obj.redirectURL;

	switch (loginMethod) {
		case Login.REDIRECT:
			location.href = url;
		break;
		case Login.IFRAME:
			LoginIframe.setSrc(url);
		break;
		case Login.POPUP:
			LoginPopup.setSrc(url);
		break;
		case Login.WINDOW:
			LoginWindow.setSrc(url);
		break;
		default:
			_logger.warn('AuthManager:onSetMvpdRedirectURL does not support loginMethod "' + loginMethod + '"');
	}
}

function onDisplayPicker(obj) {
	_logger.log('AuthManager:onDisplayPicker', arguments);

	if (MVPDPicker.isDeactivated()) {
		_logger.debug('MVPDPicker has been deactivated.');
		return;
	}

	var mvpds = obj.mvpds || [];

	// if using query param to change auth environments, some MVPDs might not approve
	mvpds = _.reject(mvpds, doesMvpdDisapprove);

	openPicker(mvpds);
}

function onResizePicker() {
	_logger.log('AuthManager:onResizePicker', arguments);

	if (config.customPicker) {
		return;
	}
}

function onAuthVendorError(obj) {
	_logger.log('AuthManager:onAuthVendorError', arguments);

	this.emit('error', obj);

	if ('data' in obj && 'level' in obj.data && obj.data.level === 'error' && 'resolution' in obj.data) {
		this.emit('resolvableError', obj.data);
	}
}

function onCreateIframe(obj) {
	_logger.log('AuthManager:onCreateIframe', arguments);

	if (loginMethod !== Login.UNDEFINED) {
		_logger.debug('AuthManager:onCreateIframe', 'did not create iframe because either AutoAuth, redirect-login or popup-login in progress -- loginMethod = "' + loginMethod + '" -- ignoring Adobe callback');
		return;
	}

	loginMethod = Login.IFRAME;

	LoginIframe.open(config, obj);
}

function onDestroyIframe() {
	_logger.log('AuthManager:onDestroyIframe', arguments);

	LoginIframe.close();
}

function login(mvpdId, redirectUrl) {
	_logger.log('AuthManager:login', arguments);

	var onLoginAuthNPassHandle;
	var onLoginAuthNFailHandle;

	function onLoginAuthNPass() {
		_logger.log('AuthManager:login', 'successfully logged in with "' + mvpdId + '"');
		this.off('authNFail', onLoginAuthNFailHandle);
		MVPDPicker.reactivate();
	}

	function onLoginAuthNFail() {
		_logger.log('AuthManager:login', 'failed to log in with "' + mvpdId + '"');
		this.off('authNPass', onLoginAuthNPassHandle);
		MVPDPicker.reactivate();
	}

	auth.once('displayProviderDialog', function () {
		_logger.log('AuthManager:login', 'intercepting request to displayProviderDialog, setting provider to "' + mvpdId + '"');
		onLoginAuthNPassHandle = this.once('authNPass', onLoginAuthNPass, this);
		onLoginAuthNFailHandle = this.once('authNFail', onLoginAuthNFail, this);
		this.openLogin(mvpdId);
	}, this);

	MVPDPicker.deactivate();
	this.getAuthentication(redirectUrl);
	// this.selectProvider(mvpdId);  -- cannot be called immediately after getAuthN()!
}


function init(options) {
	if (inited) {
		throw new Error('CVP.AuthManager.init() was already called.');
	}

	inited = true;

	// Determine Auth interface and state machines.

	// Keep a copy of what was passed in.
	this.options = options;

	// Set log level, if provided via `logLevel` config property or `authLogLevel` query param.
	if ('logLevel' in options) {
		_logger.setLevel(options.logLevel);
	}
	if ('authLogLevel' in ENV.queryParams) {
		_logger.setLevel(ENV.queryParams.authLogLevel);
	}

	// Extend a new config object, using defaults and provided options.
	_.extend(config, defaults, options);

	// Query param `authConfigEnv` = "prod | preprod", overrides config property `configEnv`.
	var authConfigEnv = ENV.getConfigOverrideParam();
	if (authConfigEnv) {
		config.configEnv = authConfigEnv;
	}

	// Sanitize refreshless config.
	config.refreshlessLogin = 'refreshlessLogin' in config && config.refreshlessLogin || false;
	config.refreshlessLogout = 'refreshlessLogout' in config && config.refreshlessLogout || false;

	// Disallow refreshless login if forceRefreshlessLogin query param is 'off'.
	if (!ENV.supports.refreshlessLogin) {
		config.refreshlessLogin = false;
	}

	// Disallow refreshless logout if forceRefreshlessLogout query param is 'off'.
	if (!ENV.supports.refreshlessLogout) {
		config.refreshlessLogout = false;
	}

	// Disallow handling login redirect if handleRedirect query param is 'off'.
	if (!ENV.supports.handleRedirect) {
		config.handleRedirect = false;
	}

	// You have to manually set picker to "default" in order to get Adobe's picker!
	config.customPicker = config.picker !== 'default';

	// Deprecation notice.
	if (!_.isUndefined(config.providerWhitelist)) {
		_logger.warn('AuthManager - NOTE: "providerWhitelist" is deprecated and will no longer be honored.');
	}

	// Deprecation notice.
	if (!_.isUndefined(config.swfStyleOverride)) {
		_logger.warn('AuthManager - NOTE: "swfStyleOverride" is deprecated and will no longer be honored.');
	}

	// Convert `clientId` to Adobe `requestorId`.
	config.requestorId = config.requestorId || config.clientId;

	// Ensure there's a `site` property, infer from `clientId` if necessary.
	config.site = config.site || getSiteId(config.clientId);

	// Setup auth interface.
	initAuth(config);

	// let AspenController register event handlers first
	AspenController.init(config, auth);

	// Install auth interface, after setting up Aspen listeners.
	auth.install();

	// wire up to the auth emitter
	// auth.on('ready', onAuthInitReady, this);
	// auth.on('initFailure', onAuthInitFailure, this);
	auth.on('ready', authNFsm.handleInitReady, authNFsm);
	auth.on('adobeInitFailure', authNFsm.handleInitFailure, authNFsm);
	authNFsm.on('ready', _.bind(onAuthInitReady, this));
	authNFsm.on('initFailure', _.bind(onAuthInitFailure, this));
	// auth.on('authNPass', onAuthNPass, this);
	// auth.on('authNFail', onAuthNFail, this);
	auth.on('authNPass', authNFsm.handleAuthNPass, authNFsm);
	auth.on('authNFail', authNFsm.handleAuthNFail, authNFsm);
	authNFsm.on('authNPass', _.bind(onAuthNPass, this));
	authNFsm.on('authNFail', _.bind(onAuthNFail, this));
	// auth.on('authZPass', onAuthZPass, this);
	// auth.on('authZFail', onAuthZFail, this);
	auth.on('authZPass', authZFsm.handleAuthZPass, authZFsm);
	auth.on('authZFail', authZFsm.handleAuthZFail, authZFsm);
	authZFsm.on('authZPass', _.bind(onAuthZPass, this));
	authZFsm.on('authZFail', _.bind(onAuthZFail, this));
	auth.on('setMetadataStatus', onMetadataStatus, this);
	auth.on('sendTrackingData', onTrackingData, this);
	auth.on('authorizationDetection', onAuthorizationDetection, this);
	auth.on('authenticationDetection', onAuthenticationDetection, this);
	auth.on('mvpdSelection', onMvpdSelection, this);
	auth.on('displayProviderDialog', onDisplayPicker, this);
	auth.on('createIFrame', onCreateIframe, this);
	auth.on('destroyIFrame', onDestroyIframe, this);
	auth.on('setMovieDimensions', onResizePicker, this);
	auth.on('errorEvent', onAuthVendorError, this);
	auth.on('preauthorizedResources', onPreauthorizedResources, this);
	auth.on('invalidProvider', onInvalidProvider, this);
	auth.on('registrationCodeValid', onRegistrationCodeValid, this);
	auth.on('registrationCodeInvalid', onRegistrationCodeInvalid, this);
	auth.on('setMvpdRedirectURL', onSetMvpdRedirectURL, this);

	// wire up Auth finite state machines
	authNFsm.init(this);
	authZFsm.init(this);

	// wire up picker
	MVPDPicker.setup(this);

	// dependence on MVPDConfig to be loaded & ready
	MVPDConfig.ready.then(function () {

		function resolveGeoReadyPromise(code) {
			geoCountry = code;
			geoReadyPromise.fulfill(code);
		}

		// request country code
		ENV.getCountryCode().then(resolveGeoReadyPromise, resolveGeoReadyPromise);

	});

	// announce ready-ness
	libReadyPromise.then(function (obj) {
		AuthManager.emit('initReady', obj);
	}, function (obj) {
		AuthManager.emit('initFailure', obj);
	});

	// close login on unload ... but not for mobile browsers, which unload when opening new window!
	if (CVP.Browser.isPlatform(CVP.Browser.DESKTOP)) {
		Dom.on(window, 'unload', function () {
			AuthManager.closeLogin();
		});
	}


	function handleLoginOpen() {
		AuthManager.emit('loginOpened');
	}

	function handleLoginClose() {
		AuthManager.emit('loginClosed');
	}

	LoginIframe.on('opened', handleLoginOpen);
	LoginIframe.on('closed', handleLoginClose);

	LoginPopup.on('opened', handleLoginOpen);
	LoginPopup.on('closed', handleLoginClose);

	LoginWindow.on('opened', handleLoginOpen);
	LoginWindow.on('closed', handleLoginClose);


	MVPDPicker.on('mvpdSelected', function onMvpdSelected(obj) {
		var mvpdId = obj.mvpdId;
		_logger.log('AuthManager:pickerMvpdSelected:event', mvpdId);
		AuthManager.emit('pickerMvpdSelected', obj);
	});
	MVPDPicker.on('darkProviderSelected', function onDarkProviderSelected(obj) {
		var mvpdId = obj.mvpdId;
		_logger.log('AuthManager:pickerDarkProviderSelected:event', mvpdId);
		AuthManager.emit('pickerDarkProviderSelected', obj);
	});
	MVPDPicker.on('helpClicked', function onHelpClicked(obj) {
		_logger.log('AuthManager:pickerHelpClicked:event', obj.state);
		AuthManager.emit('pickerHelpClicked', obj);
	});
	MVPDPicker.on('closeClicked', function onCloseClicked(obj) {
		_logger.log('AuthManager:pickerCloseClicked:event', obj.state);
		AuthManager.emit('pickerCloseClicked', obj);
	});
	MVPDPicker.on('opened', function onOpened(obj) {
		_logger.log('AuthManager:pickerOpened:event');
		nullProviderOnClose = false;
		AuthManager.emit('pickerOpened', obj);
	});
	MVPDPicker.on('canceled', function onCanceled(obj) {
		_logger.log('AuthManager:pickerCanceled:event', obj.state);
		nullProviderOnClose = true;
		getProviderID(function getProviderIDResponse(providerId) {
			obj.mvpdId = providerId;  // previously selected ID
			AuthManager.emit('pickerCanceled', obj);
			AuthManager.closePicker();
		});
	});
	MVPDPicker.on('closed', function onClosed(obj) {
		_logger.log('AuthManager:pickerClosed:event');
		AuthManager.closeLogin();
		var emitPickerClosed = _.bind(AuthManager.emit, AuthManager, 'pickerClosed', obj);
		if (nullProviderOnClose) {
			// Cancel the selected provider.
			// But wait until authNFail before emitting 'pickerClosed' event.
			AuthManager.once('authNFail', emitPickerClosed);
			AuthManager.selectProvider(null);
		}
		else {
			// Otherwise, we've probably already seen an authNPass/Fail event,
			// so fire 'pickerClosed' event immediately.
			emitPickerClosed();
		}
	});
	MVPDPicker.on('stateChange', function onStateChange(obj) {
		_logger.log('AuthManager:pickerStateChange:event', obj.state);
		AuthManager.emit('pickerStateChange', obj);
	});

	// wire up Aspen listeners to our own events

	this.on('pickerDarkProviderSelected', function onPickerDarkProviderSelected(obj) {
		AspenController.send('darkProviderSelected', {
			mvpdID: obj.mvpdId,
			success: obj.success
		});
	});

	this.on('pickerHelpClicked', function onPickerHelpClicked(/*obj*/) {
		AspenController.send('helpWindowOpened');
	});

	this.on('pickerOpened', function onPickerOpened(/*obj*/) {
		AspenController.send('pickerWindowOpened');
	});

	this.on('pickerClosed', function onPickerClosed(/*obj*/) {
		AspenController.send('pickerWindowClosed');
	});

	this.on('pickerCanceled', function onPickerCanceled(obj) {
		AspenController.send('cancelLogin', {
			mvpdID: obj.mvpdId,
			requestorID: auth.getRequestorId()
		});
	});

	// wire up config callbacks to our own events

	if (_.isFunction(config.onInitReady)) {
		this.on('initReady', function callOnInitReady() {
			_logger.log('AuthManager:onInitReady:callback');
			config.onInitReady();
		});
	}

	if (_.isFunction(config.onInitFailure)) {
		this.on('initFailure', function callOnInitFailure() {
			_logger.log('AuthManager:onInitFailure:callback');
			config.onInitFailure();
		});
	}

	if (_.isFunction(config.onAuthenticationPassed)) {
		this.on('authNPass', function callOnAuthenticationPassed(obj) {
			_logger.log('AuthManager:onAuthNPass:callback', arguments);
			config.onAuthenticationPassed(obj.mvpdId, obj.mvpdConfig);
		});
	}

	if (_.isFunction(config.onAuthenticationFailed)) {
		this.on('authNFail', function callOnAuthenticationFailed(obj) {
			_logger.log('AuthManager:onAuthNFail:callback', arguments);
			config.onAuthenticationFailed(obj.errorCode, obj.errorString, obj.errorHtml);
		});
	}

	if (_.isFunction(config.onPreauthorizedResource)) {
		this.on('preauthorizedResource', function callOnPreauthorizedResource(obj) {
			_logger.log('AuthManager:onPreauthorizedResource:callback', arguments);
			config.onPreauthorizedResource(obj.resource);
		});
	}

	if (_.isFunction(config.onPreauthorizedResources)) {
		this.on('preauthorizedResources', function callOnPreauthorizedResources(obj) {
			_logger.log('AuthManager:onPreauthorizedResources:callback', arguments);
			config.onPreauthorizedResources(obj.resources);
		});
	}

	if (_.isFunction(config.onAuthorizationPassed)) {
		this.on('authZPass', function callOnAuthorizationPassed(obj) {
			_logger.log('AuthManager:onAuthZPass:callback', arguments);
			config.onAuthorizationPassed(obj.resource);
		});
	}

	if (_.isFunction(config.onAuthorizationFailed)) {
		this.on('authZFail', function callOnAuthorizationFailed(obj) {
			_logger.log('AuthManager:onAuthZFail:callback', arguments);
			config.onAuthorizationFailed(obj.resource, obj.errorCode, obj.errorString, obj.errorHtml);
		});
	}

	if (_.isFunction(config.onMetadataStatus)) {
		this.on('metadataStatus', function callOnMetadataStatus(obj) {
			_logger.log('AuthManager:onMetadataStatus:callback', arguments);
			config.onMetadataStatus(obj.key, obj.value);
		});
	}

	if (_.isFunction(config.onTrackingData)) {
		this.on('trackingData', function callOnTrackingData(obj) {
			_logger.log('AuthManager:onTrackingData:callback', arguments);
			config.onTrackingData(obj.eventType, obj.trackingData);
		});
	}

	if (_.isFunction(config.onAuthorizationDetection)) {
		this.on('authorizationDetection', function callOnAuthorizationDetection(obj) {
			_logger.log('AuthManager:onAuthorizationDetection:callback', arguments);
			if (!obj.success) {
				config.onAuthorizationDetection(obj.success);
			}
			else {
				config.onAuthorizationDetection(obj.success, obj.mvpdId, obj.userId, obj.cached);
			}
		});
	}

	if (_.isFunction(config.onAuthenticationDetection)) {
		this.on('authenticationDetection', function callOnAuthenticationDetection(obj) {
			_logger.log('AuthManager:onAuthenticationDetection:callback', arguments);
			if (!obj.success) {
				config.onAuthenticationDetection(obj.success);
			}
			else {
				config.onAuthenticationDetection(obj.success, obj.mvpdId, obj.userId, obj.cached);
			}
		});
	}

	if (_.isFunction(config.onMvpdSelection)) {
		this.on('mvpdSelection', function callOnMvpdSelection(obj) {
			_logger.log('AuthManager:onMvpdSelection:callback', arguments);
			config.onMvpdSelection(obj.mvpdId);
		});
	}

	if (_.isFunction(config.onRegistrationCodeValid)) {
		this.on('registrationCodeValid', function callOnRegistrationCodeValid(obj) {
			_logger.log('AuthManager:onRegistrationCodeValid:callback', arguments);
			config.onRegistrationCodeValid(obj.regCode);
		});
	}

	if (_.isFunction(config.onRegistrationCodeInvalid)) {
		this.on('registrationCodeInvalid', function callOnRegistrationCodeInvalid() {
			_logger.log('AuthManager:onRegistrationCodeInvalid:callback', arguments);
			config.onRegistrationCodeInvalid();
		});
	}

	if (_.isFunction(config.onError)) {
		this.on('error', function callOnError(obj) {
			_logger.log('AuthManager:onError:callback', arguments);
			config.onError(obj.source, obj.data);
		});
	}

	if (_.isFunction(config.onResolvableError)) {
		this.on('resolvableError', function callOnResolvableError(obj) {
			_logger.log('AuthManager:onResolvableError:callback', arguments);
			config.onResolvableError(obj.data);
		});
	}

	if (_.isFunction(config.onLoginOpened)) {
		this.on('loginOpened', function callOnLoginOpened() {
			_logger.log('AuthManager:onLoginOpened:callback', arguments);
			config.onLoginOpened();
		});
	}

	if (_.isFunction(config.onLoginClosed)) {
		this.on('loginClosed', function callOnLoginClosed() {
			_logger.log('AuthManager:onLoginClosed:callback', arguments);
			config.onLoginClosed();
		});
	}

	if (_.isFunction(config.onPickerOpened)) {
		this.on('pickerOpened', function callOnPickerOpened() {
			_logger.log('AuthManager:onPickerOpened:callback', arguments);
			config.onPickerOpened();
		});
	}

	if (_.isFunction(config.onPickerClosed)) {
		this.on('pickerClosed', function callOnPickerClosed() {
			_logger.log('AuthManager:onPickerClosed:callback', arguments);
			config.onPickerClosed();
		});
	}

	if (_.isFunction(config.onPickerCanceled)) {
		this.on('pickerCanceled', function callOnPickerCanceled(obj) {
			_logger.log('AuthManager:onPickerCanceled:callback', arguments);
			config.onPickerCanceled(obj.state);
		});
	}

	if (_.isFunction(config.onPickerStateChange)) {
		this.on('pickerStateChange', function callOnPickerStateChange(obj) {
			_logger.log('AuthManager:onPickerStateChange:callback', arguments);
			config.onPickerStateChange(obj.state);
		});
	}

	if (_.isFunction(config.onPickerHelpClicked)) {
		this.on('pickerHelpClicked', function callOnPickerHelpClicked(obj) {
			_logger.log('AuthManager:onPickerHelpClicked:callback', arguments);
			config.onPickerHelpClicked(obj.state);
		});
	}

	if (_.isFunction(config.onPickerCloseClicked)) {
		this.on('pickerCloseClicked', function callOnPickerCloseClicked(obj) {
			_logger.log('AuthManager:onPickerCloseClicked:callback', arguments);
			config.onPickerCloseClicked(obj.state);
		});
	}

	if (_.isFunction(config.onPickerMvpdSelected)) {
		this.on('pickerMvpdSelected', function callOnPickerMvpdSelected(obj) {
			_logger.log('AuthManager:onPickerMvpdSelected:callback', arguments);
			config.onPickerMvpdSelected(obj.mvpdId);
		});
	}

	return this;
}

function fsm_getInitErrors() {
	_logger.log('AuthManager.getInitErrors()');

	return authNFsm.getInitErrors();
}

function fsm_checkAuthentication() {
	_logger.log('AuthManager.checkAuthentication()');

	_.defer(_.bind(authNFsm.checkAuthN, authNFsm));
}

function fsm_getAuthentication(redirectUrl) {
	_logger.log('AuthManager.getAuthentication(redirectUrl)', arguments);

	_.defer(_.bind(authNFsm.getAuthN, authNFsm, redirectUrl));
}

function fsm_checkPreauthorizedResource(resourceId) {
	_logger.log('AuthManager.checkPreauthorizedResource(resourceId)', arguments);

	if (!isReady) return notifyNotReady();

	if (doesCurrentMvpdIdDisapprove()) {
		return notifyDisapproved();
	}

	_.defer(_.bind(authNFsm.checkPreauthZ, authNFsm, resourceId));
}

function fsm_checkPreauthorizedResources(resourceIds) {
	_logger.log('AuthManager.checkPreauthorizedResources(resourceIds)', arguments);

	if (!isReady) return notifyNotReady();

	if (doesCurrentMvpdIdDisapprove()) {
		return notifyDisapproved();
	}

	_.defer(_.bind(authNFsm.checkPreauthZ, authNFsm, resourceIds));
}

function fsm_checkAuthorization(resourceId, feature) {
	_logger.log('AuthManager.checkAuthorization(resourceId, feature)', arguments);

	if (!isReady) return notifyNotReady();

	if (doesCurrentMvpdIdDisapprove()) {
		return notifyDisapproved();
	}

	if (!isAuthNed) {
		_logger.warn('AuthManager.checkAuthorization should only be called if you are authenticated');

		var errorCode = 'N000';
		var errorString = 'Attempted to authorize with a provider that was not found in MVPD config.';

		var data = {
			mvpdId: null,
			resource: resourceId,
			errorCode: errorCode,
			errorString: errorString
		};

		this.emit('authZFail', data);

		this.emit('errorEvent', {
			source: 'cvp',
			data: {
				errorId: errorCode,
				level: 'error',
				message: errorString
			}
		});

		return;
	}

	_.defer(_.bind(authZFsm.checkAuthZ, authZFsm, resourceId, feature));
}

function awaitAuthNResponse() {
	var promise = new Promise();

	var onRequestAuthNPassHandle;
	var onRequestAuthNFailHandle;

	function onRequestAuthNPass(data) {
		this.off('authNFail', onRequestAuthNFailHandle, this);
		promise.fulfill(data);
	}

	function onRequestAuthNFail(data) {
		this.off('authNPass', onRequestAuthNPassHandle, this);
		promise.reject(data);
	}

	onRequestAuthNPassHandle = this.once('authNPass', onRequestAuthNPass, this);
	onRequestAuthNFailHandle = this.once('authNFail', onRequestAuthNFail, this);

	return promise;
}

function awaitAuthZResponse() {
	var promise = new Promise();

	var onRequestAuthZPassHandle;
	var onRequestAuthZFailHandle;

	function onRequestAuthZPass(data) {
		this.off('authZFail', onRequestAuthZFailHandle, this);
		promise.fulfill(data);
	}

	function onRequestAuthZFail(data) {
		this.off('authZPass', onRequestAuthZPassHandle, this);
		promise.reject(data);
	}

	onRequestAuthZPassHandle = this.once('authZPass', onRequestAuthZPass, this);
	onRequestAuthZFailHandle = this.once('authZFail', onRequestAuthZFail, this);

	return promise;
}

function fsm_requestAuthN(resourceId) {
	_logger.log('AuthManager.requestAuthN(resourceId)', arguments);

	if (!isReady) {
		notifyNotReady();
		return Promise.reject();
	}

	if (doesCurrentMvpdIdDisapprove()) {
		notifyDisapproved();
		return Promise.reject();
	}

	var promise = this.awaitAuthNResponse();

	this.checkAuthentication(resourceId);

	return promise;
}

function fsm_requestAuthZ(resourceId, feature) {
	_logger.log('AuthManager.requestAuthZ(resourceId, feature)', arguments);

	if (!isReady) {
		notifyNotReady();
		return Promise.reject();
	}

	if (doesCurrentMvpdIdDisapprove()) {
		notifyDisapproved();
		return Promise.reject();
	}

	var promise = this.awaitAuthZResponse();

	this.checkAuthorization(resourceId, feature);

	return promise.then(function (data) {
		var result = {
			resourceId: resourceId
		};

		if (feature) {
			result.feature = feature;
		}

		result.token = data.token;

		return result;
	});
}

function fsm_logout() {
	_logger.log('AuthManager.logout()');

	_.defer(_.bind(authNFsm.logout, authNFsm));
}

_.extend(AuthManager, {

	version: App.VERSION,
	FLASH: FLASH,
	HTML5: HTML5,
	CLIENTLESS: CLIENTLESS,
	Resolution: App.Resolution,
	Strings: App.Strings,

	init: init,
	ready: libReadyPromise,
	getInitErrors: fsm_getInitErrors,

	getEnv: getEnv,
	getMVPDs: getMVPDs,

	// isAuthenticated: isAuthenticated,
	isAuthenticated: fsm_checkAuthentication,
	checkAuthentication: fsm_checkAuthentication,
	// getAuthentication: getAuthentication,
	getAuthentication: fsm_getAuthentication,

	isVisibleMvpdId: isVisibleMvpdId,
	isValidMvpdId: isValidMvpdId,
	getProvider: getProvider,
	getProviderID: getProviderID,
	getMvpdConfigEntry: getMvpdConfigEntry,

	// checkPreauthorizedResource: checkPreauthorizedResources,
	checkPreauthorizedResource: fsm_checkPreauthorizedResource,
	// checkPreauthorizedResources: checkPreauthorizedResources,
	checkPreauthorizedResources: fsm_checkPreauthorizedResources,

	// checkAuthorization: checkAuthorization,
	checkAuthorization: fsm_checkAuthorization,

	requestAuthN: fsm_requestAuthN,
	requestAuthZ: fsm_requestAuthZ,
	awaitAuthNResponse: awaitAuthNResponse,
	awaitAuthZResponse: awaitAuthZResponse,

	checkRegistrationCode: checkRegistrationCode,
	getMetadata: getMetadata,
	getFeatureList: getFeatureList,
	checkFeature: checkFeature,
	getAccessToken: getAccessToken,

	// logout: logout,
	logout: fsm_logout,

	login: login,
	getRememberedProvider: _.bind(RememberedProvider.get, RememberedProvider),

	// internal use only
	_checkPreauthZ: checkPreauthorizedResources,
	_checkAuthN: checkAuthentication,
	_getAuthN: getAuthentication,
	_checkAuthZ: checkAuthorization,
	_logout: logout,

	// not publicized
	MVPDPicker: MVPDPicker,
	_: _,
	Util: Util,
	Dom: Dom,
	Promise: Promise,
	when: Promise.all,
	Store: Store,
	getCountryCode: getCountryCode,

	getConfigString: getConfigString,
	openLogin: openLogin,
	openPicker: openPicker,
	getMvpdById: getMvpdById,
	selectProvider: selectProvider,
	closeLogin: closeLogin,
	isPickerOpen: _.bind(MVPDPicker.isOpen, MVPDPicker),
	closePicker: closePicker,
	cancelPicker: closePicker

});


module.exports = AuthManager;
